import { PRO_SUBSCRIPTION_PERKS } from "@/constants";
import { useAppNavigation } from "@/features/Navigation/hooks/useAppNavigation";
import { usePermissions } from "@/features/Permissions/hooks/usePermissions";
import { UpsellModal } from "@/features/Subscriptions/components/UpsellModal/UpsellModal";
import { TeamNavigation } from "@/features/Teams/components/TeamNavigation/TeamNavigation";
import { TemplateModal } from "@/features/Templates/components/TemplateModal";
import { createWorkspace } from "@/features/Workspaces/actions/createWorkspace";
import { useWorkspaces, useWorkspaceSubscription } from "@/features/Workspaces/hooks/useWorkspaces";
import { Illustrations } from "@/illustrations";
import { create_workspace_event, open_template_modal_event } from "@/services/posthog/events";
import { useAnalytics } from "@/services/posthog/hooks";
import { Box, Flex, IconButton, Stack, Text, Tooltip, useDisclosure } from "@chakra-ui/react";
import { Icon, PromptStudioLogo, Sidebar, SidebarCategoryTitle, SidebarItem } from "@pufflig/prompt-studio-ui";
import { useParams, useSearchParams } from "react-router-dom";

export const NavSidebar = () => {
  const { workspaceId: selectedWorkspaceId } = useParams();
  const [searchParams] = useSearchParams();
  const teamId = searchParams.get("team") || undefined;

  const { data: workspaces } = useWorkspaces(teamId);
  const { data: permissions } = usePermissions(teamId);
  const canCreateWorkspaces = permissions?.canCreateWorkspaces;

  const {
    isOpen: isOpenWorkspaceUpsellModal,
    onOpen: onOpenWorkspaceUpsellModal,
    onClose: onCloseWorkspaceUpsellModal,
  } = useDisclosure();

  const { navigateToWorkspace, navigateToHome, navigateToData, isHome, isOnDataPage } = useAppNavigation();
  const { isOpen: isOpenTemplateModal, onOpen: onOpenTemplateModal, onClose: onCloseTemplateModal } = useDisclosure();
  const analytics = useAnalytics();

  useWorkspaceSubscription();

  return (
    <>
      <Sidebar>
        <Flex paddingLeft="1rem" marginTop="0.18rem" gap="spacing-xs" height="26px" onClick={() => {}} cursor="pointer">
          <Flex width="26px">
            <PromptStudioLogo />
          </Flex>
          <Text textStyle={"brand"} fontSize="2xl">
            Prompt Studio
          </Text>
        </Flex>
        <Stack gap="spacing-xs" marginBottom="spacing-xs">
          <Box mt="spacing-xxl" mb="spacing-xl">
            <TeamNavigation />
          </Box>
          <SidebarItem isSelected={isHome()} Icon={<Icon name="home-2" />} label="Home" onClick={navigateToHome} />
          <SidebarItem
            Icon={<Icon name="note" />}
            label="Templates"
            onClick={() => {
              analytics.capture(open_template_modal_event, { from: "sidebar" });
              onOpenTemplateModal();
            }}
          />
          <SidebarItem isSelected={isOnDataPage()} Icon={<Icon name="data" />} label="Data" onClick={navigateToData} />
          <SidebarCategoryTitle
            Icon={
              <Tooltip label="Add workspace" aria-label="Add workspace">
                <IconButton
                  onClick={
                    canCreateWorkspaces
                      ? async () => {
                          analytics.capture(create_workspace_event, { from: "sidebar" });
                          const { workspaceId } = await createWorkspace("My Workspace", teamId || undefined);
                          if (workspaceId) navigateToWorkspace(workspaceId);
                        }
                      : onOpenWorkspaceUpsellModal
                  }
                  size="sm"
                  variant="ghost"
                  icon={<Icon name="add" />}
                  aria-label="add workspace"
                />
              </Tooltip>
            }
            label="Workspaces"
          />
        </Stack>
        <Stack gap="spacing-xs" overflow="auto" flex="1">
          {workspaces?.map((workspace) => (
            <SidebarItem
              tooltip={workspace.name || "Untitled workspace"}
              key={workspace.workspace_id}
              isSelected={workspace.workspace_id === selectedWorkspaceId}
              Icon={<Icon name="folder-2" />}
              label={workspace.name || "Untitled workspace"}
              onClick={() => {
                navigateToWorkspace(workspace.workspace_id);
              }}
            />
          ))}
        </Stack>
      </Sidebar>
      <UpsellModal
        title="Multiple workspaces"
        descriptions={[
          "You’ve reached the limit of one workspace.",
          "Upgrade your Prompt Studio account to Pro plan to create unlimited workspaces and organize your work better.",
          "Plus, enjoy...",
        ]}
        features={PRO_SUBSCRIPTION_PERKS}
        Image={<Illustrations.ImageAddWorkspace />}
        isOpen={isOpenWorkspaceUpsellModal}
        onClose={onCloseWorkspaceUpsellModal}
      />
      <TemplateModal isOpen={isOpenTemplateModal} onClose={onCloseTemplateModal} onOpen={onOpenTemplateModal} />
    </>
  );
};
